export interface TagBox {
  /** Offset from the container's left content edge, in px. */
  left: number;
  /** Offset from the container's top content edge, in px, rounded to a row. */
  top: number;
  width: number;
}

interface GetVisibleTagsCountParams {
  /** Boxes of every tag, in DOM order, measured while all tags are rendered. */
  boxes: TagBox[];
  containerWidth: number;
  gap: number;
  maxRows: number;
  /** Width of the "+N" button, measured with its widest possible label. */
  moreButtonWidth: number;
}

/**
 * Decides how many tags stay visible once the list is clipped to `maxRows`.
 *
 * The browser has already wrapped every tag, so rows are read back from the
 * measured `top` offsets instead of re-implementing flexbox packing. Tags that
 * land past `maxRows` are dropped; if any tag is dropped, the "+N" button has
 * to share the last visible row with the tags that stay, so trailing tags are
 * dropped until it fits after the last one.
 *
 * Flex items are packed in DOM order, so the visible tags are always a prefix
 * and dropping a trailing tag never reflows the ones before it.
 */
export const getVisibleTagsCount = ({
  boxes,
  containerWidth,
  gap,
  maxRows,
  moreButtonWidth,
}: GetVisibleTagsCountParams): number => {
  if (boxes.length === 0) {
    return 0;
  }

  const rowTops: number[] = [];

  boxes.forEach(({ top }) => {
    if (!rowTops.includes(top)) {
      rowTops.push(top);
    }
  });

  rowTops.sort((first, second) => first - second);

  const fittingCount = boxes.filter(
    ({ top }) => rowTops.indexOf(top) < maxRows
  ).length;

  if (fittingCount === boxes.length) {
    return boxes.length;
  }

  for (let lastIndex = fittingCount - 1; lastIndex >= 0; lastIndex--) {
    const { left, width } = boxes[lastIndex];
    const freeSpace = containerWidth - (left + width) - gap;

    if (freeSpace >= moreButtonWidth) {
      return lastIndex + 1;
    }
  }

  return 0;
};
